import {
	type CreateMessageInput,
	type Message,
	MessageStatus,
	MessageType,
	type UpdateMessageInput,
} from '@/domain/message.types'
import type { Selection } from '@/domain/work-order.types'

/** Editable form state for the message create/edit form. */
export interface MessageFormState {
	title: string
	description: string
	content: string
	status: MessageStatus
	languageIso: string
	messageType: MessageType
	/** Classification tuple attached to the message (empty = none). */
	classification: Selection
	/** `order_code` attribute values — the work orders this copy is ON (empty = none). */
	orderCodes: string[]
}

export const EMPTY_FORM: MessageFormState = {
	title: '',
	description: '',
	content: '',
	status: MessageStatus.DRAFT,
	languageIso: 'en-us',
	messageType: MessageType.COPY_OUT,
	classification: {},
	orderCodes: [],
}

/** Hydrate the form from an existing message (classification and order codes are loaded separately). */
export function fromMessage(m: Message, classification: Selection = {}, orderCodes: string[] = []): MessageFormState {
	return {
		title: m.title,
		description: m.description ?? '',
		content: m.content,
		status: m.status,
		languageIso: m.languageIso,
		messageType: m.messageType,
		classification,
		orderCodes,
	}
}

/** Map the form to the create payload (drops empty optional description). */
export function toCreateInput(form: MessageFormState): CreateMessageInput {
	return {
		title: form.title.trim(),
		description: form.description.trim() || undefined,
		content: form.content,
		status: form.status,
		languageIso: form.languageIso,
		messageType: form.messageType,
	}
}

/** Map the form to a minimal update patch. */
export function toUpdateInput(form: MessageFormState): UpdateMessageInput {
	return {
		title: form.title.trim(),
		description: form.description.trim() || undefined,
		content: form.content,
		status: form.status,
		languageIso: form.languageIso,
		messageType: form.messageType,
	}
}
